import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout, { DASHBOARDS } from './components/Layout';
import AssistantPanel from './components/assistant/AssistantPanel';
import { AssistantProvider, useAssistantPageExtras } from './components/assistant/AssistantContext';
import Overview from './components/dashboards/Overview';
import DealsHome from './meeting/DealsHome';
import Verdict from './meeting/Verdict';
import Forecast from './meeting/Forecast';
import Value from './meeting/Value';
import Finance from './meeting/Finance';
import PackageStep from './meeting/PackageStep';
import ChecklistPanel from './diligence/ChecklistPanel';
import DataRoomPanel from './diligence/DataRoomPanel';
import Executive from './components/dashboards/Executive';
import Reserves from './components/dashboards/Reserves';
import CashFlow from './components/dashboards/CashFlow';
import Wells from './components/dashboards/Wells';
import Land from './components/dashboards/Land';
import Diligence from './components/dashboards/Diligence';
import Sensitivity from './components/dashboards/Sensitivity';
import Geoscience from './components/dashboards/Geoscience';
import Decision from './components/dashboards/Decision';
import MapDashboard from './components/dashboards/Map';
import WellDetail from './components/dashboards/WellDetail';
import AssetWorkflow from './assets/AssetWorkflow';
import QuickScreen from './screen/QuickScreen';
import DetailDrawer from './components/DetailDrawer';
import { AuthProvider, useAuth } from './auth/AuthContext';
import Login, { ForgotPassword } from './auth/Login';
import ResetPassword from './auth/ResetPassword';
import AcceptEula from './auth/AcceptEula';
import { SetupTwoFactor, TwoFactorChallenge } from './auth/TwoFactor';
import AdminUsers from './auth/AdminUsers';
import CrmApp from './crm/CrmApp';
import InvestorPortal from './investor/InvestorPortal';
import AbsDesk, { ABS_TABS, _clearAbsScreenCache } from './abs/AbsDesk';
import { clearMeetingAbsCache } from './meeting/absCache.js';
import { applyStatedAsk } from './engine/deskBid.js';
import { DealProvider, useDeal } from './deals/DealContext';
import { dealDataUrl, normalizeHashPath, parseDealFromHash, withDealQuery } from './deals/registry';
import {
  normalizeDeskRoute,
  touchLoginAt,
  writeLastDeskLocation,
} from './deals/deskSession';
import { normalizePath, posthog, posthogEnabled } from './lib/posthog';
import { track, trackException } from './lib/track';
import {
  MISSING_DESK_DATA_MESSAGE,
  fetchDeskJson,
  isExpectedMissingDeskData,
  reportDeskJsonFailure,
} from './lib/deskDataFetch';
import {
  shouldCommitDeskLoadFailure,
  shouldBlockOnDeskLoadError,
  shouldClearStickyDeskLoadOnCommit,
} from './auth/sessionRefresh';
import DashboardErrorBoundary from './components/DashboardErrorBoundary';
import SessionIntentSurvey from './components/SessionIntentSurvey';
import { loadDeskKnobsForNav, setDeskDealContext, splitHash, DESK_QUERY_KEY_SET, clearLiveDeskKnobs } from './engine/deskPersistence';
import { deskShellResetState, resetDeskJsonCacheRefs } from './lib/deskJsonCache';
import { hashWithContext, parseSelectionContext } from './utils/selectionContext';
import { hasActiveDrill } from './utils/drilldown';
import { withAroEstimate } from './aro/api.js';

const DASHBOARD_COMPONENTS = {
  overview: Overview,
  decision: Decision,
  executive: Executive,
  reserves: Reserves,
  cashflow: CashFlow,
  wells: Wells,
  map: MapDashboard,
  land: Land,
  sensitivity: Sensitivity,
  geoscience: Geoscience,
  diligence: Diligence,
};

/** JSON reference tabs to prefetch — Overview/Package are brief/shell routes (no *.json). */
const PREFETCH_KEYS = Object.keys(DASHBOARD_COMPONENTS).filter((k) => k !== 'overview');
/** Meeting path (redesign): five deal steps rendered from DealPackage + prefetched JSON. */
const MEETING_KEYS = new Set(['analysis', 'forecast', 'value', 'finance', 'package']);
const DEAL_TOOL_KEYS = new Set(['checklist', 'dataroom']);
/** Projects / deals are shell routes. They have no `<tab>.json` under /data. */
const NO_JSON_DASH_KEYS = new Set(['projects', 'deals']);
const SHELL_DASH_KEYS = new Set([
  'overview',
  'assets',
  'screen',
  ...MEETING_KEYS,
  ...DEAL_TOOL_KEYS,
  ...NO_JSON_DASH_KEYS,
]);
const ABS_TAB_KEYS = new Set(ABS_TABS.map((t) => t.key));
const deskJsonTrackSinks = { track, trackException };

/** Rewrite a legacy `#verdict…` hash to `#analysis…` in place (no new history entry). */
function legacyStepHash(rawFull) {
  return String(rawFull || '').replace(/^\/?verdict(?=$|[?&/])/, 'analysis');
}
function redirectLegacyStepHash(rawFull) {
  const next = legacyStepHash(rawFull);
  if (next === rawFull || typeof window === 'undefined') return;
  try {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#${next}`);
  } catch {
    /* routing still resolves via the alias below */
  }
}

function parseHash() {
  const rawFull = window.location.hash.replace(/^#/, '');
  // Desk knobs may ride on the hash query (#decision?hh=0&bid=13) — strip for routing.
  const { path: rawPath } = splitHash(rawFull);
  // `#/projects` (leading slash) must route like `#projects`.
  const raw = normalizeHashPath(rawPath);
  if (raw.startsWith('reset')) {
    const q = rawFull.includes('?') ? rawFull.slice(rawFull.indexOf('?') + 1) : '';
    const token = new URLSearchParams(q).get('token') || '';
    return { view: 'reset', token, dash: 'executive', wellId: '', appMode: 'dataroom', absTab: 'summary' };
  }
  if (raw === 'admin' || raw.startsWith('admin')) {
    return { view: 'admin', token: '', dash: 'admin', wellId: '', appMode: 'dataroom', absTab: 'summary' };
  }
  if (raw === 'crm' || raw.startsWith('crm')) {
    return { view: 'crm', token: '', dash: 'crm', wellId: '', appMode: 'dataroom', absTab: 'summary' };
  }
  if (raw === 'portal' || raw.startsWith('portal')) {
    return { view: 'portal', token: '', dash: 'portal', wellId: '', appMode: 'dataroom', absTab: 'summary' };
  }
  if (raw === 'forgot') {
    return { view: 'forgot', token: '', dash: 'executive', wellId: '', appMode: 'dataroom', absTab: 'summary' };
  }
  if (raw === 'projects' || raw === 'deals' || raw.startsWith('projects/') || raw.startsWith('deals/')) {
    return { view: 'projects', token: '', dash: 'projects', wellId: '', appMode: 'dataroom', absTab: 'summary' };
  }

  // ABS deep-links: #abs | #abs/summary | #mode=abs | #mode=abs/coverage
  if (raw === 'abs' || raw.startsWith('abs/') || raw.startsWith('mode=abs')) {
    let absTab = 'summary';
    if (raw.startsWith('abs/')) {
      const seg = raw.slice(4).split(/[/?#]/)[0];
      if (ABS_TAB_KEYS.has(seg)) absTab = seg;
    } else if (raw.startsWith('mode=abs')) {
      const rest = raw.slice('mode=abs'.length);
      if (rest.startsWith('/')) {
        const seg = rest.slice(1).split(/[/?#]/)[0];
        if (ABS_TAB_KEYS.has(seg)) absTab = seg;
      }
    }
    return { view: 'abs', token: '', dash: 'abs', wellId: '', appMode: 'abs', absTab };
  }

  // Core asset workflow (asset model / DCA / map v2 / review) — shell route, own API.
  if (raw === 'assets' || raw.startsWith('assets/')) {
    return { view: 'app', token: '', dash: 'assets', wellId: '', appMode: 'dataroom', absTab: 'summary' };
  }

  // Post-login landing is the Deals home (meeting redesign). A bare hash lands there; a bare
  // `#?deal=x` still opens that deal. `#dashboard` (#369 landing) and `#screen` both open the
  // full all-deals quick screen (ranked table + buy-box criteria editor) inside the shell.
  if (raw === '' && !rawFull.includes('deal=')) {
    return { view: 'projects', token: '', dash: 'projects', wellId: '', appMode: 'dataroom', absTab: 'summary' };
  }
  if (raw === 'dashboard' || raw === 'screen') {
    return { view: 'app', token: '', dash: 'screen', wellId: '', appMode: 'dataroom', absTab: 'summary' };
  }

  if (raw.startsWith('well/')) {
    let wellId = raw.slice('well/'.length);
    // Drop any accidental query suffix on well ids.
    wellId = wellId.split('?')[0];
    try {
      wellId = decodeURIComponent(wellId);
    } catch {
      /* keep raw segment */
    }
    return { view: 'app', token: '', dash: 'well', wellId, appMode: 'dataroom', absTab: 'summary' };
  }
  // Step 1 was renamed Verdict → Analysis (Oct 18): old #verdict links redirect to #analysis.
  if (raw === 'verdict') redirectLegacyStepHash(rawFull);
  // Default deal landing is the Analysis step; legacy #overview / #verdict links land there too.
  const dash = MEETING_KEYS.has(raw) || DEAL_TOOL_KEYS.has(raw)
    ? raw
    : raw === 'overview' || raw === 'verdict' || !raw
      ? 'analysis'
      : raw in DASHBOARD_COMPONENTS
        ? raw
        : 'analysis';
  const selection = parseSelectionContext(rawFull);
  return {
    view: 'app',
    token: '',
    dash,
    wellId: '',
    appMode: 'dataroom',
    absTab: 'summary',
    concernId: selection.concernId,
    selectedWellId: selection.selectedWellId,
    selectedMonth: selection.selectedMonth,
    scenarioId: selection.scenarioId,
  };
}

function AppShell() {
  const auth = useAuth();
  const {
    deal,
    dealId,
    selectDeal,
    showProjectSelector,
    openProjectSelector,
    closeProjectSelector,
  } = useDeal();
  const { clearPageExtras } = useAssistantPageExtras();
  const [route, setRoute] = useState(parseHash);
  const [authScreen, setAuthScreen] = useState('login');
  const [dataByKey, setDataByKey] = useState({});
  const [dealPackage, setDealPackage] = useState(null);
  const [dealPackageFailed, setDealPackageFailed] = useState(false);
  /** Dashboard keys that already failed after retry — avoid effect re-fetch loops. */
  const [loadFailedByKey, setLoadFailedByKey] = useState({});
  /** Overview brief waits on diligence+executive settle (loaded or confirmed absent). */
  const [overviewExtrasReady, setOverviewExtrasReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prefetch mutates dataByKey / dealPackage; active-tab load must read them
  // without putting the objects in effect deps (that remounts + cancels).
  // Keep refs in sync on commit (not only on render) so a late active-tab
  // catch sees prefetch success that has not re-rendered yet.
  const dataByKeyRef = useRef({});
  const loadFailedByKeyRef = useRef({});
  const activeKeyRef = useRef('');
  /** True only when the active route is a real JSON dashboard tab (not well/shell/ABS). */
  const isJsonDashboardTabRef = useRef(false);

  const dataFile = useCallback(
    (name) => dealDataUrl(deal, name, import.meta.env.BASE_URL),
    [deal],
  );

  const showWell = route.dash === 'well';
  const showAbs = route.appMode === 'abs' || route.view === 'abs';
  // Prefer hash for projects: after a landing-card click the hash is already
  // overview?deal=… while route.view may still be 'projects' for one frame.
  // Never keep the full-page picker open on a stale route alone.
  const hashSaysProjects = parseDealFromHash(
    typeof window !== 'undefined' ? window.location.hash : '',
  ).viewProjects;
  // Bare hash (post-login) also lands on Deals — the landing page.
  const rawHashNow = typeof window !== 'undefined' ? window.location.hash.replace(/^#\/?/, '') : '';
  const hashIsBare = rawHashNow === '' || (rawHashNow.startsWith('?') && !rawHashNow.includes('deal='));
  const showProjects =
    showProjectSelector || (route.view === 'projects' && (hashSaysProjects || hashIsBare));
  const activeKey = showWell ? 'wells' : showAbs ? 'abs' : route.dash;
  activeKeyRef.current = activeKey;
  const showAdmin = route.view === 'admin';
  const showCrm = route.view === 'crm';
  const showPortal = auth.isInvestor;
  // ABS is internal desk only — hide from investor role.
  const showAbsMode = Boolean(auth.isAuthed && !auth.isInvestor);
  // Well detail remaps activeKey → 'wells' but loading is deal-package-driven
  // (see shell/well effect). Sticky clear must only run for JSON tabs.
  isJsonDashboardTabRef.current =
    !showWell && !showAbs && !showAdmin && !showCrm && !SHELL_DASH_KEYS.has(activeKey);

  /** Store desk JSON and sync refs immediately; clear sticky failure for that key. */
  const commitDeskJson = useCallback((key, payload, { replace = false } = {}) => {
    if (!payload) return false;
    const current = dataByKeyRef.current;
    if (!replace && current[key]) return false;
    dataByKeyRef.current = { ...current, [key]: payload };
    setDataByKey((prev) => {
      if (!replace && prev[key]) return prev;
      return { ...prev, [key]: payload };
    });
    if (loadFailedByKeyRef.current[key]) {
      const nextFailed = { ...loadFailedByKeyRef.current };
      delete nextFailed[key];
      loadFailedByKeyRef.current = nextFailed;
      setLoadFailedByKey(nextFailed);
    }
    // Prefer stored JSON over sticky failure on JSON tabs (#155). Do not clear
    // when well detail remapped activeKey to 'wells' — that loading/error is
    // deal-package-driven (Bugbot 79fd756c).
    if (
      shouldClearStickyDeskLoadOnCommit({
        committedKey: key,
        activeKey: activeKeyRef.current,
        isJsonDashboardTab: isJsonDashboardTabRef.current,
      })
    ) {
      setError(null);
      setLoading(false);
    }
    return true;
  }, []);

  // Investors always land in the portal — never the full VDR / ABS.
  useEffect(() => {
    if (!auth.isAuthed || !auth.isInvestor) return;
    if (route.view !== 'portal' && route.view !== 'reset') {
      window.location.hash = 'portal';
    }
  }, [auth.isAuthed, auth.isInvestor, route.view]);

  /** Clear desk JSON refs + React state together (deal switch / logout). */
  const resetDeskCaches = useCallback(() => {
    resetDeskJsonCacheRefs(dataByKeyRef, loadFailedByKeyRef);
    const shell = deskShellResetState();
    setDataByKey(shell.dataByKey);
    setLoadFailedByKey(shell.loadFailedByKey);
    setDealPackage(shell.dealPackage);
    setDealPackageFailed(shell.dealPackageFailed);
    setOverviewExtrasReady(shell.overviewExtrasReady);
    setError(shell.error);
    setLoading(shell.loading);
  }, []);

  // Drop gated desk caches when the session is no longer an internal desk user
  // (logout, investor bounce, incomplete auth) so a prior 401 cannot stick.
  useEffect(() => {
    if (auth.isAuthed && auth.isInternal) return;
    resetDeskCaches();
    // Bugbot TP: per-deal ABS screen cache must not survive logout into the next session.
    _clearAbsScreenCache();
    clearMeetingAbsCache();
  }, [auth.isAuthed, auth.isInternal, resetDeskCaches]);

  // If a non-investor somehow loses ABS eligibility, bounce to Data Room Overview.
  useEffect(() => {
    if (!auth.isAuthed || auth.isInvestor) return;
    if (showAbs && !showAbsMode) {
      window.location.hash = 'overview';
    }
  }, [auth.isAuthed, auth.isInvestor, showAbs, showAbsMode]);

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Manual pageviews for hash routes (capture_pageview is off), with a paired
  // completion event so named-user reports can show time on each dashboard.
  useEffect(() => {
    if (!posthogEnabled || !auth.isAuthed) return;
    const path = normalizePath(window.location.hash || route.dash);
    const dashboard = showAdmin
      ? 'admin'
      : showCrm
        ? 'crm'
        : showPortal
          ? 'portal'
          : showAbs
            ? `abs:${route.absTab || 'summary'}`
            : activeKey;
    const startedAt = Date.now();
    let visibleSince = document.visibilityState === 'visible' ? startedAt : null;
    let activeDurationMs = 0;
    let ended = false;

    const addVisibleDuration = () => {
      if (visibleSince === null) return;
      activeDurationMs += Date.now() - visibleSince;
      visibleSince = null;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        visibleSince = Date.now();
      } else {
        addVisibleDuration();
      }
    };
    const completeView = (endReason) => {
      if (ended) return;
      ended = true;
      addVisibleDuration();
      const durationMs = Date.now() - startedAt;
      // Avoid React StrictMode's development-only mount/unmount probe.
      if (durationMs < 1000) return;
      track('dashboard_view_ended', {
        dashboard,
        path,
        role: auth.user?.role,
        duration_seconds: Math.round(durationMs / 100) / 10,
        active_duration_seconds: Math.round(activeDurationMs / 100) / 10,
        end_reason: endReason,
      });
    };
    const onPageHide = () => completeView('pagehide');

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    posthog.capture('$pageview', { $current_url: `${window.location.origin}${path}` });
    track('dashboard_viewed', {
      dashboard,
      path,
      role: auth.user?.role,
    });
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      completeView('route_change');
    };
  }, [
    route.view,
    route.dash,
    auth.isAuthed,
    auth.user?.role,
    showAdmin,
    showCrm,
    showPortal,
    showAbs,
    route.absTab,
    activeKey,
  ]);

  // Sync deal from hash `?deal=` and `#projects` / `#/projects` on every hash change
  // (including same-view deal swaps where route.view/dash are unchanged).
  //
  // Critical: only open the selector when the *hash* says projects — never when
  // stale `route.view === 'projects'` after selectDeal updates dealId but before
  // React applies the new hash route. That race re-calls openProjectSelector()
  // and pins the user on the picker.
  const dealIdRef = useRef(dealId);
  dealIdRef.current = dealId;

  useEffect(() => {
    const syncDealFromHash = () => {
      const { viewProjects, dealIdFromHash } = parseDealFromHash(window.location.hash);
      if (viewProjects) {
        openProjectSelector();
      }
      if (dealIdFromHash && dealIdFromHash !== dealIdRef.current) {
        selectDeal(dealIdFromHash);
      } else if (!viewProjects && dealIdFromHash) {
        // A deal-scoped link (e.g. #analysis?deal=…) always leaves the Deals list.
        closeProjectSelector();
      } else if (!viewProjects && /^#\/?(crm|admin|portal)\b/.test(window.location.hash)) {
        // Workspace routes (Investors / Admin) leave the Deals list too.
        closeProjectSelector();
      }
    };
    syncDealFromHash();
    window.addEventListener('hashchange', syncDealFromHash);
    window.addEventListener('popstate', syncDealFromHash);
    return () => {
      window.removeEventListener('hashchange', syncDealFromHash);
      window.removeEventListener('popstate', syncDealFromHash);
    };
  }, [selectDeal, openProjectSelector, closeProjectSelector]);

  // Persist last login + last desk location for Select-project briefing (per user).
  useEffect(() => {
    if (!auth.isAuthed || !auth.user?.id || auth.isInvestor) return;
    touchLoginAt(auth.user.id);
  }, [auth.isAuthed, auth.user?.id, auth.isInvestor]);

  useEffect(() => {
    if (!auth.isAuthed || !auth.user?.id || auth.isInvestor || showProjects) return;
    const routePath = normalizeDeskRoute(
      showAbs
        ? `abs/${route.absTab || 'summary'}`
        : showAdmin
          ? 'admin'
          : showCrm
            ? 'crm'
            : showWell
              ? `well/${route.wellId}`
              : activeKey,
    );
    if (!routePath) return;
    writeLastDeskLocation(auth.user.id, { dealId, route: routePath });
  }, [
    auth.isAuthed,
    auth.user?.id,
    auth.isInvestor,
    showProjects,
    dealId,
    activeKey,
    showAbs,
    showAdmin,
    showCrm,
    showWell,
    route.absTab,
    route.wellId,
  ]);

  const prevDealIdRef = useRef(dealId);

  useEffect(() => {
    setDeskDealContext(dealId);
    const dealChanged = prevDealIdRef.current !== dealId;
    if (dealChanged) {
      clearLiveDeskKnobs();
      // Drop desk knob query params so Eagle HH cannot ride onto Juno WTI (or reverse).
      if (typeof window !== 'undefined' && typeof history !== 'undefined') {
        const { path, query } = splitHash(window.location.hash);
        const params = new URLSearchParams(query || '');
        let stripped = false;
        for (const k of DESK_QUERY_KEY_SET) {
          if (params.has(k)) {
            params.delete(k);
            stripped = true;
          }
        }
        if (stripped) {
          const q = params.toString();
          const next = q ? `${path}?${q}` : path;
          history.replaceState(
            null,
            '',
            `${window.location.pathname}${window.location.search}#${next}`,
          );
        }
      }
      prevDealIdRef.current = dealId;
    }
    // Always clear refs + state together — stale refs skip fetch / block commit.
    resetDeskCaches();
    if (dealChanged) setLoading(true);
  }, [dealId, resetDeskCaches]);

  const handleNavigate = useCallback(
    (key, opts = {}) => {
      clearPageExtras();
      track('nav_clicked', { dashboard: key, deal_id: dealId });
      if (key === 'admin') {
        window.location.hash = 'admin';
        return;
      }
      if (key === 'crm') {
        window.location.hash = 'crm';
        return;
      }
      if (key === 'portal') {
        window.location.hash = 'portal';
        return;
      }
      if (key === 'projects' || key === 'deals') {
        openProjectSelector();
        window.scrollTo({ top: 0 });
        return;
      }
      if (key === 'abs') {
        window.location.hash = withDealQuery('abs/summary', dealId);
        window.scrollTo({ top: 0 });
        return;
      }
      const toolKey = typeof key === 'string' ? key.split('?')[0] : '';
      if (MEETING_KEYS.has(key) || DEAL_TOOL_KEYS.has(toolKey)) {
        window.location.hash = withDealQuery(key, dealId);
        window.scrollTo({ top: 0 });
        return;
      }
      if (typeof key === 'string' && key.startsWith('well/')) {
        window.location.hash = withDealQuery(key, dealId);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // Live desk edits > hash deep-link > prior-session storage (#138),
      // plus optional selection context (Overview concern → source, #113).
      const knobs = loadDeskKnobsForNav({}, { dealId });
      const hash =
        opts.hash ||
        hashWithContext(key, {
          knobs,
          selection: opts.selection,
          preserveExisting: !opts.selection,
        });
      window.location.hash = withDealQuery(hash, dealId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [clearPageExtras, dealId, openProjectSelector],
  );

  const handleAbsTab = useCallback(
    (tab) => {
      clearPageExtras();
      track('abs_tab_clicked', { tab, deal_id: dealId });
      window.location.hash = withDealQuery(`abs/${tab}`, dealId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [clearPageExtras, dealId],
  );

  // Prefetch all dashboard JSON + DealPackage so the assistant has page context everywhere.
  // Investors / unauthenticated sessions must not hit gated /data/*.json (nginx 401 → alert noise).
  useEffect(() => {
    if (!auth.isAuthed || auth.isInvestor || !auth.isInternal || showProjects) return;
    let cancelled = false;
    const refreshSession = () => auth.refresh();
    PREFETCH_KEYS.forEach((key) => {
      // Prefer refs — deal-switch clears refs synchronously in resetDeskCaches
      // before this effect; state `dataByKey` may still hold the prior deal.
      if (dataByKeyRef.current[key]) return;
      fetchDeskJson(dataFile(`${key}.json`), { refreshSession })
        .then((data) => {
          if (!cancelled && data) {
            commitDeskJson(key, data);
          }
        })
        .catch(() => {
          /* soft prefetch — active-tab load reports failures */
        });
    });
    // Always (re)fetch deal-package on deal/auth — resetDeskCaches nulls state, but
    // this closure may still see the prior deal's package from the render before reset.
    // decision.json carries the published ask (sellerAskMM). The package often does not,
    // so the ask is copied on once that file arrives. It must not block the package.
    fetchDeskJson(dataFile('deal-package.json'), {
      refreshSession,
      errorMessage: (status) => `deal-package.json ${status}`,
    })
      .then((pkg) => {
        if (cancelled || !pkg) return;
        setDealPackage(pkg);
        setDealPackageFailed(false);
        // Configurable ARO → overview brief (Decision/Sensitivity merge it on their own load).
        if (auth.isInternal) {
          withAroEstimate(pkg, dealId).then((next) => {
            if (cancelled || next === pkg) return;
            setDealPackage((cur) => {
              if (!cur) return next;
              const ask = cur.meta?.sellerAskMM;
              if (ask == null || next.meta?.sellerAskMM != null) return next;
              return { ...next, meta: { ...(next.meta || {}), sellerAskMM: ask } };
            });
          });
        }
        fetchDeskJson(dataFile('decision.json'), { refreshSession })
          .catch(() => null)
          .then((decision) => {
            if (cancelled || !decision) return;
            setDealPackage((cur) => (cur ? applyStatedAsk(cur, decision.sellerAskMM) : cur));
          });
      })
      .catch((err) => {
        if (!cancelled) {
          setDealPackageFailed(true);
          reportDeskJsonFailure(
            err,
            {
              event: 'deal_package_load_failed',
              context: 'deal_package_prefetch',
              resource: 'deal-package.json',
              deal_id: dealId,
            },
            deskJsonTrackSinks,
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // intentionally only on auth/deal — dataByKey/dealPackage updates shouldn't re-fire prefetch storms
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthed, auth.isInvestor, auth.isInternal, dealId, showProjects, dataFile, commitDeskJson]);

  // Overview brief inputs: wait for diligence + executive to settle (loaded or absent).
  useEffect(() => {
    if (!auth.isAuthed || auth.isInvestor || !auth.isInternal || showProjects) return;
    if (activeKey !== 'overview') return;
    if (overviewExtrasReady) return;
    let cancelled = false;
    const refreshSession = () => auth.refresh();
    const loadExtra = (key) =>
      fetchDeskJson(dataFile(`${key}.json`), { refreshSession })
        .then((payload) => {
          if (!cancelled && payload) {
            commitDeskJson(key, payload);
          }
        })
        .catch(() => {});
    Promise.all([
      dataByKey.diligence ? Promise.resolve() : loadExtra('diligence'),
      dataByKey.executive ? Promise.resolve() : loadExtra('executive'),
    ]).finally(() => {
      if (!cancelled) setOverviewExtrasReady(true);
    });
    return () => {
      cancelled = true;
    };
    // Settle once per Overview visit; dataByKey presence only gates the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthed, auth.isInvestor, auth.isInternal, activeKey, overviewExtrasReady, dealId, showProjects, dataFile, commitDeskJson]);

  // Shell / well loading — may depend on dealPackage settle from prefetch.
  // Kept separate so package/extras updates do not cancel an in-flight JSON tab load.
  useEffect(() => {
    if (!auth.isAuthed || auth.isInvestor || !auth.isInternal || showProjects) return;
    if (showAbs) {
      setLoading(false);
      setError(null);
      return;
    }
    if (showAdmin || showCrm) return;
    const refreshSession = () => auth.refresh();
    if (showWell) {
      // Well detail needs deal-package; map/geoscience enrich via prefetch.
      ['map', 'geoscience'].forEach((key) => {
        if (dataByKeyRef.current[key]) return;
        fetchDeskJson(dataFile(`${key}.json`), { refreshSession })
          .then((payload) => {
            if (payload) commitDeskJson(key, payload);
          })
          .catch(() => {});
      });
      if (dealPackageFailed && !dealPackage) {
        setLoading(false);
        setError('Failed to load deal-package.json — well detail unavailable.');
        return;
      }
      setLoading(!dealPackage);
      setError(null);
      return;
    }
    // Overview / Package are shell routes: no dedicated *.json; Overview uses DealPackage + diligence.
    if (SHELL_DASH_KEYS.has(activeKey)) {
      if (activeKey === 'overview') {
        const packageReady = Boolean(dealPackage) || dealPackageFailed;
        setLoading(!(packageReady && overviewExtrasReady));
        setError(null);
        return;
      }
      setLoading(false);
      setError(null);
    }
  }, [
    activeKey,
    auth.isAuthed,
    auth.isInternal,
    auth.isInvestor,
    showAdmin,
    showCrm,
    showWell,
    showAbs,
    showProjects,
    dealPackage,
    dealPackageFailed,
    overviewExtrasReady,
    auth.refresh,
    commitDeskJson,
    dealId,
    dataFile,
  ]);

  // Active JSON tab load. Cancel only when tab/auth/route intentionally changes —
  // never because sibling prefetch mutated dataByKey / dealPackage.
  useEffect(() => {
    if (!auth.isAuthed || auth.isInvestor || !auth.isInternal || showProjects) return;
    if (showAbs || showAdmin || showCrm || showWell) return;
    if (SHELL_DASH_KEYS.has(activeKey)) return;

    if (dataByKeyRef.current[activeKey]) {
      setLoading(false);
      setError(null);
      return;
    }
    if (loadFailedByKeyRef.current[activeKey]) {
      setLoading(false);
      setError(loadFailedByKeyRef.current[activeKey]);
      return;
    }

    let cancelled = false;
    const loadKey = activeKey;
    const refreshSession = () => auth.refresh();
    setLoading(true);
    setError(null);
    fetchDeskJson(dataFile(`${loadKey}.json`), {
      refreshSession,
      errorMessage: (status) => `Failed to load ${loadKey}.json (${status})`,
    })
      .then((data) => {
        if (cancelled) return;
        commitDeskJson(loadKey, data, { replace: true });
        setLoading(false);
      })
      .catch((err) => {
        // Prefetch (or a prior success) may already hold the body — never stamp a
        // permanent loadFailedByKey after a successful payload was received.
        if (
          !shouldCommitDeskLoadFailure({
            cancelled,
            alreadyHasData: Boolean(dataByKeyRef.current[loadKey]),
          })
        ) {
          if (!cancelled && dataByKeyRef.current[loadKey]) {
            setLoading(false);
            setError(null);
          }
          return;
        }
        const missing = isExpectedMissingDeskData(err?.status);
        if (!missing) console.error(err);
        const message = missing
          ? MISSING_DESK_DATA_MESSAGE
          : err.message || `Failed to load ${loadKey}.json`;
        setError(message);
        setLoading(false);
        setLoadFailedByKey((prev) => {
          if (prev[loadKey]) return prev;
          const next = { ...prev, [loadKey]: message };
          loadFailedByKeyRef.current = next;
          return next;
        });
        reportDeskJsonFailure(
          err,
          {
            event: 'dashboard_load_failed',
            dashboard: loadKey,
            context: 'dashboard_data_load',
            resource: `${loadKey}.json`,
            deal_id: dealId,
          },
          deskJsonTrackSinks,
        );
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeKey,
    auth.isAuthed,
    auth.isInternal,
    auth.isInvestor,
    showAdmin,
    showCrm,
    showWell,
    showAbs,
    showProjects,
    auth.refresh,
    commitDeskJson,
    dealId,
    dataFile,
  ]);

  // Desk rendered without load error — funnel step between login and meaningful action.
  const readyKeyRef = useRef('');
  useEffect(() => {
    if (!auth.isAuthed || auth.isInvestor) return;
    const hasStoredJson =
      Boolean(dataByKey[activeKey]) && !showWell && !SHELL_DASH_KEYS.has(activeKey);
    if (!showAbs && (loading || error) && !hasStoredJson) return;
    let key = '';
    let props = null;
    if (showAdmin) {
      key = 'admin';
      props = { dashboard: 'admin', path: '/admin' };
    } else if (showAbs) {
      key = `abs:${route.absTab || 'summary'}`;
      props = { dashboard: 'abs', abs_tab: route.absTab || 'summary', path: `/abs/${route.absTab || 'summary'}` };
    } else if (showCrm) {
      key = 'crm';
      props = { dashboard: 'crm', path: '/crm' };
    } else if (showWell) {
      if (!dealPackage) return;
      key = `well:${route.wellId || ''}`;
      props = { dashboard: 'well', well_id: route.wellId || undefined, path: '/well' };
    } else if (SHELL_DASH_KEYS.has(activeKey)) {
      if (activeKey === 'overview' && ((!dealPackage && !dealPackageFailed) || !overviewExtrasReady)) {
        return;
      }
      key = activeKey;
      props = { dashboard: activeKey, path: normalizePath(window.location.hash || activeKey) };
    } else if (dataByKey[activeKey]) {
      key = activeKey;
      props = { dashboard: activeKey, path: normalizePath(window.location.hash || activeKey) };
    }
    if (!key || !props || readyKeyRef.current === key) return;
    readyKeyRef.current = key;
    track('dashboard_ready', props);
  }, [
    auth.isAuthed,
    auth.isInvestor,
    loading,
    error,
    showAdmin,
    showCrm,
    showAbs,
    showWell,
    dealPackage,
    dealPackageFailed,
    overviewExtrasReady,
    activeKey,
    dataByKey,
    route.wellId,
    route.absTab,
  ]);

  const ActiveComponent = showWell || showAbs ? null : DASHBOARD_COMPONENTS[activeKey];
  const data = dataByKey[activeKey];
  // Prefer already-stored JSON over a sticky load error (Bugbot eb532da2).
  const preferStoredJson =
    Boolean(data) && !showWell && !SHELL_DASH_KEYS.has(activeKey);
  const showLoadError = shouldBlockOnDeskLoadError({
    error,
    hasStoredData: preferStoredJson,
  });
  const selectionContext = useMemo(
    () => ({
      concernId: route.concernId || '',
      selectedWellId: route.selectedWellId || '',
      selectedMonth: route.selectedMonth || '',
      scenarioId: route.scenarioId || '',
    }),
    [route.concernId, route.selectedWellId, route.selectedMonth, route.scenarioId],
  );
  const showDrillDrawer =
    !showAdmin &&
    !showCrm &&
    !showAbs &&
    !showWell &&
    auth.isAuthed &&
    hasActiveDrill(selectionContext);
  const dashLabel = useMemo(() => {
    if (showAdmin) return 'Admin';
    if (showAbs) return `ABS · ${route.absTab || 'summary'}`;
    if (showCrm) return 'CRM';
    if (showPortal) return 'Investor portal';
    if (showWell) return 'Well detail';
    return DASHBOARDS.find((d) => d.key === activeKey)?.label || activeKey;
  }, [activeKey, showAdmin, showCrm, showPortal, showWell, showAbs, route.absTab]);

  if (auth.bootstrapping) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="loading-spinner" />
          <p>Checking session…</p>
        </div>
      </div>
    );
  }

  if (route.view === 'reset' && route.token) {
    return (
      <ResetPassword
        token={route.token}
        onDone={() => {
          window.location.hash = '';
          setRoute(parseHash());
        }}
      />
    );
  }

  if (!auth.user) {
    if (authScreen === 'forgot' || route.view === 'forgot') {
      return <ForgotPassword onBack={() => setAuthScreen('login')} />;
    }
    return <Login onForgot={() => setAuthScreen('forgot')} />;
  }

  if (auth.pending2fa) return <TwoFactorChallenge />;
  if (auth.mustReset) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1>Password reset required</h1>
          <p className="auth-lead">Use Forgot password on the sign-in screen, or open your invite link.</p>
          <button type="button" className="auth-primary" onClick={() => auth.logout()}>
            Sign out
          </button>
        </div>
      </div>
    );
  }
  if (auth.needs2faSetup) return <SetupTwoFactor />;
  if (auth.needsEula) return <AcceptEula />;

  if (auth.isInvestor) {
    return <InvestorPortal user={auth.user} onLogout={() => auth.logout()} />;
  }

  if (showProjects) {
    return (
      <Layout
        activeKey="deals"
        workspace="deals"
        onNavigate={handleNavigate}
        user={auth.user}
        isAdmin={auth.isAdmin}
        isInternal={auth.isInternal}
        onLogout={() => auth.logout()}
      >
        <DashboardErrorBoundary dashboard="deals" key="deals-home">
          <DealsHome
            onOpenDeal={(id) => {
              selectDeal(id);
              closeProjectSelector();
              window.location.hash = withDealQuery('analysis', id);
              window.scrollTo({ top: 0 });
            }}
            onOpenScreen={() => {
              closeProjectSelector();
              window.location.hash = 'screen';
              window.scrollTo({ top: 0 });
            }}
          />
        </DashboardErrorBoundary>
        <AssistantPanel
          dashboardKey="deals"
          dashboardLabel="Deals"
          pageData={{ surface: 'deals' }}
          dealPackage={null}
          executive={null}
          selectionContext={selectionContext}
        />
      </Layout>
    );
  }

  return (
    <Layout
      activeKey={showAdmin ? 'admin' : showCrm ? 'crm' : showAbs ? 'abs' : showWell ? 'well' : activeKey}
      workspace={showAdmin ? 'admin' : showCrm ? 'investors' : activeKey === 'screen' ? 'deals' : 'deal'}
      onNavigate={handleNavigate}
      user={auth.user}
      isAdmin={auth.isAdmin}
      isInternal={auth.isInternal}
      packageMeta={dealPackage?.meta || null}
      onLogout={() => auth.logout()}
      deal={deal}
      currentPath={
        showAbs
          ? `abs/${route.absTab || 'summary'}`
          : showWell
            ? `well/${route.wellId}`
            : activeKey
      }
    >
      {showAdmin ? (
        auth.isAdmin ? (
          <AdminUsers />
        ) : (
          <div className="error-screen">
            <h2>Admin only</h2>
            <p>Your role does not include the user admin tool.</p>
          </div>
        )
      ) : showAbs ? (
        <DashboardErrorBoundary dashboard="abs" key={`abs-${dealId}`}>
          <AbsDesk tab={route.absTab || 'summary'} onNavigateTab={handleAbsTab} dealId={dealId} />
        </DashboardErrorBoundary>
      ) : showCrm ? (
        auth.isInternal ? (
          <CrmApp />
        ) : (
          <div className="error-screen">
            <h2>Internal only</h2>
            <p>The investor CRM is for Firebrand / desk users.</p>
          </div>
        )
      ) : (
        <>
          {loading && !preferStoredJson && (
            <div className="loading-screen">
              <div className="loading-spinner" />
              <p>Loading {dashLabel}{showWell ? '…' : ' dashboard…'}</p>
            </div>
          )}
          {showLoadError && (
            <div className="error-screen">
              <h2>{error === MISSING_DESK_DATA_MESSAGE ? 'Data not published yet' : 'Data not found'}</h2>
              <p>{error}</p>
            </div>
          )}
          {!loading && !showLoadError && showWell && (
            <DashboardErrorBoundary dashboard="well" key={`well-${route.wellId}`}>
              <WellDetail
                wellId={route.wellId}
                dealPackage={dealPackage}
                mapData={dataByKey.map}
                geoscienceData={dataByKey.geoscience}
                onNavigate={handleNavigate}
              />
            </DashboardErrorBoundary>
          )}
          {!showWell && DEAL_TOOL_KEYS.has(activeKey) && (
            <DashboardErrorBoundary dashboard={activeKey} key={`${activeKey}-${dealId}`}>
              {activeKey === 'checklist' ? (
                <ChecklistPanel
                  dealId={dealId}
                  deal={deal}
                  pkg={dealPackage}
                  diligence={dataByKey.diligence}
                  wells={dataByKey.wells}
                  land={dataByKey.land}
                  map={dataByKey.map}
                  geoscience={dataByKey.geoscience}
                  onNavigate={handleNavigate}
                />
              ) : (
                <DataRoomPanel dealId={dealId} onNavigate={handleNavigate} />
              )}
            </DashboardErrorBoundary>
          )}
          {!showWell && MEETING_KEYS.has(activeKey) && (
            <DashboardErrorBoundary dashboard={activeKey} key={`${activeKey}-${dealId}`}>
              {(() => {
                const MeetingStep = {
                  analysis: Verdict,
                  forecast: Forecast,
                  value: Value,
                  finance: Finance,
                  package: PackageStep,
                }[activeKey];
                return (
                  <MeetingStep
                    dealId={dealId}
                    deal={deal}
                    dealPackage={dealPackage}
                    dealPackageFailed={dealPackageFailed}
                    diligence={dataByKey.diligence}
                    executive={dataByKey.executive}
                    reserves={dataByKey.reserves}
                    wells={dataByKey.wells}
                    land={dataByKey.land}
                    map={dataByKey.map}
                    geoscience={dataByKey.geoscience}
                    onNavigate={handleNavigate}
                  />
                );
              })()}
            </DashboardErrorBoundary>
          )}
          {!loading && !showLoadError && !showWell && activeKey === 'assets' && (
            <DashboardErrorBoundary dashboard="assets" key={`assets-${dealId}`}>
              <AssetWorkflow />
            </DashboardErrorBoundary>
          )}
          {!loading && !showLoadError && !showWell && activeKey === 'screen' && (
            <DashboardErrorBoundary dashboard="screen" key="screen">
              <QuickScreen />
            </DashboardErrorBoundary>
          )}
          {!loading && preferStoredJson && ActiveComponent && (
            <DashboardErrorBoundary dashboard={activeKey} key={`${dealId}-${activeKey}`}>
              <ActiveComponent
                data={data}
                mapData={dataByKey.map}
                onNavigate={handleNavigate}
                selectionContext={selectionContext}
              />
            </DashboardErrorBoundary>
          )}
          {showDrillDrawer ? (
            <DetailDrawer
              selection={selectionContext}
              surface={activeKey}
              dealPackage={dealPackage}
              pageData={
                activeKey === 'overview'
                  ? { diligence: dataByKey.diligence, executive: dataByKey.executive }
                  : data
              }
              mapData={dataByKey.map}
              geoscienceData={dataByKey.geoscience}
            />
          ) : null}
        </>
      )}
      <AssistantPanel
        dashboardKey={
          showAdmin ? 'admin' : showAbs ? 'abs' : showCrm ? 'crm' : showWell ? 'well' : activeKey
        }
        dashboardLabel={showCrm ? 'Investor CRM' : showAbs ? 'ABS Desk' : dashLabel}
        pageData={
          showAdmin
            ? { role: auth.user?.role, admin: true }
            : showAbs
              ? { surface: 'abs', absTab: route.absTab }
              : showCrm
                ? { surface: 'crm' }
                : activeKey === 'overview'
                  ? { surface: 'overview', concernId: route.concernId }
                  : data
        }
        dealPackage={dealPackage}
        executive={dataByKey.executive}
        selectionContext={selectionContext}
      />
      <SessionIntentSurvey user={auth.user} enabled={auth.isAuthed && !auth.isInvestor} />
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <DealProvider>
        <AssistantProvider>
          <AppShell />
        </AssistantProvider>
      </DealProvider>
    </AuthProvider>
  );
}
